if (!customElements.get('podcast-details-tabs')) {
  customElements.define(
    'podcast-details-tabs',
    class PodcastDetailsTabs extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('[data-podcast-details-tab]'));
        this.panels = Array.from(this.querySelectorAll('[data-podcast-details-panel]'));
        this.shareContainer = this.querySelector('.podcast-details-tabs__share');
        this.shareButton = this.querySelector('[data-podcast-details-share]');
        this.sharePopover = this.querySelector('[data-podcast-details-share-popover]');
        this.shareLinks = Array.from(this.querySelectorAll('[data-podcast-details-share-link]'));
        this.copyLinkButtons = Array.from(this.querySelectorAll('[data-podcast-details-copy-link]'));
        this.status = this.querySelector('[data-podcast-details-status]');
        this.desktopShareQuery = window.matchMedia('(min-width: 990px)');
        this.shareAnimationDuration = 300;
        this.shareAnimationFrame = null;
        this.shareAnimationTimeout = null;
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
        this.onShareQueryChange = this.onShareQueryChange.bind(this);

        this.tabs.forEach((tab) => {
          tab.addEventListener('click', () => this.setActiveTab(tab.dataset.podcastDetailsTab));
          tab.addEventListener('keydown', (event) => this.onTabKeydown(event));
        });

        this.updateShareLinks();

        if (this.shareButton) {
          this.shareButton.addEventListener('click', () => this.onShareButtonClick());
        }

        this.copyLinkButtons.forEach((button) => {
          button.addEventListener('click', () => this.copyCurrentLink());
        });

        if (this.desktopShareQuery.addEventListener) {
          this.desktopShareQuery.addEventListener('change', this.onShareQueryChange);
        } else if (this.desktopShareQuery.addListener) {
          this.desktopShareQuery.addListener(this.onShareQueryChange);
        }
      }

      disconnectedCallback() {
        cancelAnimationFrame(this.shareAnimationFrame);
        clearTimeout(this.shareAnimationTimeout);
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onDocumentKeydown);

        if (this.desktopShareQuery.addEventListener) {
          this.desktopShareQuery.removeEventListener('change', this.onShareQueryChange);
        } else if (this.desktopShareQuery.removeListener) {
          this.desktopShareQuery.removeListener(this.onShareQueryChange);
        }
      }

      setActiveTab(tabName, focusTab = false) {
        this.tabs.forEach((tab) => {
          const isActive = tab.dataset.podcastDetailsTab === tabName;

          tab.classList.toggle('is-active', isActive);
          tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
          tab.tabIndex = isActive ? 0 : -1;

          if (isActive && focusTab) {
            tab.focus();
          }
        });

        this.panels.forEach((panel) => {
          const isActive = panel.dataset.podcastDetailsPanel === tabName;

          panel.classList.toggle('is-active', isActive);
          panel.hidden = !isActive;
        });
      }

      onTabKeydown(event) {
        const currentIndex = this.tabs.indexOf(event.currentTarget);
        let nextIndex = currentIndex;

        if (event.key === 'ArrowRight') {
          nextIndex = currentIndex + 1 >= this.tabs.length ? 0 : currentIndex + 1;
        } else if (event.key === 'ArrowLeft') {
          nextIndex = currentIndex - 1 < 0 ? this.tabs.length - 1 : currentIndex - 1;
        } else if (event.key === 'Home') {
          nextIndex = 0;
        } else if (event.key === 'End') {
          nextIndex = this.tabs.length - 1;
        } else {
          return;
        }

        event.preventDefault();
        this.setActiveTab(this.tabs[nextIndex].dataset.podcastDetailsTab, true);
      }

      async shareEpisode() {
        const shareData = {
          title: document.title,
          url: this.currentShareUrl,
        };

        if (navigator.share) {
          try {
            await navigator.share(shareData);
            this.setStatus(this.dataset.shareSuccess || 'Shared');
            return;
          } catch (error) {
            if (error.name === 'AbortError') return;
          }
        }

        await this.copyToClipboard(shareData.url);
        this.setStatus(this.dataset.copySuccess || 'Link copied');
      }

      onShareButtonClick() {
        if (this.sharePopover && this.desktopShareQuery.matches) {
          this.toggleSharePopover();
          return;
        }

        this.shareEpisode();
      }

      async copyCurrentLink() {
        await this.copyToClipboard(this.currentShareUrl);
        this.setStatus(this.dataset.copySuccess || 'Link copied');
        this.closeSharePopover();
      }

      toggleSharePopover() {
        if (!this.sharePopover) return;

        if (this.sharePopover.hidden || this.sharePopover.classList.contains('is-closing')) {
          this.openSharePopover();
        } else {
          this.closeSharePopover();
        }
      }

      openSharePopover() {
        cancelAnimationFrame(this.shareAnimationFrame);
        clearTimeout(this.shareAnimationTimeout);
        this.updateShareLinks();
        this.sharePopover.hidden = false;
        this.sharePopover.classList.remove('is-closing');
        this.shareAnimationFrame = requestAnimationFrame(() => {
          this.sharePopover.classList.add('is-open');
        });
        this.shareButton.setAttribute('aria-expanded', 'true');
        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onDocumentKeydown);
      }

      closeSharePopover() {
        if (!this.sharePopover || this.sharePopover.hidden) return;

        cancelAnimationFrame(this.shareAnimationFrame);
        clearTimeout(this.shareAnimationTimeout);
        this.sharePopover.classList.remove('is-open');
        this.sharePopover.classList.add('is-closing');
        this.shareButton.setAttribute('aria-expanded', 'false');
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onDocumentKeydown);

        this.shareAnimationTimeout = setTimeout(() => {
          this.sharePopover.hidden = true;
          this.sharePopover.classList.remove('is-closing');
        }, this.shareAnimationDuration);
      }

      onDocumentClick(event) {
        if (this.shareContainer && this.shareContainer.contains(event.target)) return;

        this.closeSharePopover();
      }

      onDocumentKeydown(event) {
        if (event.key !== 'Escape') return;

        this.closeSharePopover();
        this.shareButton.focus();
      }

      onShareQueryChange(event) {
        if (!event.matches) {
          this.closeSharePopover();
        }
      }

      updateShareLinks() {
        const url = encodeURIComponent(this.currentShareUrl);
        const title = encodeURIComponent(document.title);

        this.shareLinks.forEach((link) => {
          const type = link.dataset.podcastDetailsShareLink;

          if (type === 'email') {
            link.href = `mailto:?subject=${title}&body=${url}`;
          } else if (type === 'x') {
            link.href = `https://twitter.com/intent/tweet?url=${url}&text=${title}`;
          } else if (type === 'linkedin') {
            link.href = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
          } else if (type === 'facebook') {
            link.href = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
          }
        });
      }

      get currentShareUrl() {
        return window.location.href;
      }

      async copyToClipboard(text) {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text);
          return;
        }

        const input = document.createElement('input');
        input.value = text;
        input.setAttribute('readonly', '');
        input.style.position = 'absolute';
        input.style.left = '-9999px';
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        input.remove();
      }

      setStatus(message) {
        if (!this.status) return;

        this.status.textContent = message;
      }
    }
  );
}
